<template>
    <div class="meta-insights-performance">
        <div class="header">
            <div class="title-block">
                <div class="title">{{ $t("metaInsights.performanceTitle") }}</div>
                <div class="subtitle">{{ $t("metaInsights.performanceSubtitle") }}</div>
            </div>
            <div class="toolbar">
                <a-select v-model="metric" style="width: 160px;" @change="onMetricChange">
                    <a-select-option value="impressions">
                        {{ $t("metaInsights.kpi.impressions") }}
                    </a-select-option>
                    <a-select-option value="reach">{{ $t("metaInsights.kpi.reach") }}</a-select-option>
                    <a-select-option value="clicks">{{ $t("metaInsights.kpi.clicks") }}</a-select-option>
                    <a-select-option value="spend">{{ $t("metaInsights.kpi.spend") }}</a-select-option>
                </a-select>
                <DateRangePicker v-model="dateRange" @change="onDateRangeChange" />
                <SupsisButton ghost @click="loadAll" :disabled="loading">
                    <i class="material-icons" :class="{ spinning: loading }">refresh</i>
                    {{ $t("metaInsights.refresh") }}
                </SupsisButton>
            </div>
        </div>

        <div class="grid">
            <BreakdownChart
                :title="$t('metaInsights.breakdown.publisherPlatform')"
                :rows="platformRows"
                breakdown-key="publisher_platform"
                :metric="metric"
                chart-type="pie"
                :loading="loadingPlatform"
                :label-map="platformLabels"
                :currency="currency"
            />
            <BreakdownChart
                :title="$t('metaInsights.breakdown.platformPosition')"
                :rows="positionRows"
                breakdown-key="platform_position"
                :metric="metric"
                chart-type="bar"
                :loading="loadingPosition"
                :currency="currency"
            />
            <BreakdownChart
                :title="$t('metaInsights.breakdown.device')"
                :rows="deviceRows"
                breakdown-key="impression_device"
                :metric="metric"
                chart-type="bar"
                :loading="loadingDevice"
                :currency="currency"
            />
        </div>
    </div>
</template>

<script>
import _ from "lodash";
import DateRangePicker from "./DateRangePicker.vue";
import BreakdownChart from "./BreakdownChart.vue";
import SupsisButton from "../../../Settings/components/SupsisButton.vue";
import { INSIGHTS_API } from "../../../../../api/meta";

export default {
    name: "Performance",
    components: {
        DateRangePicker,
        BreakdownChart,
        SupsisButton,
    },
    props: {
        channel: { type: Object, required: true },
    },
    data() {
        return {
            dateRange: { datePreset: "last_30d" },
            metric: "impressions",
            platformRows: [],
            positionRows: [],
            deviceRows: [],
            loadingPlatform: false,
            loadingPosition: false,
            loadingDevice: false,
        };
    },
    computed: {
        currency() {
            return _.get(this.channel, "payload.selectedAdAccount.currency", "");
        },
        loading() {
            return this.loadingPlatform || this.loadingPosition || this.loadingDevice;
        },
        platformLabels() {
            return {
                facebook: "Facebook",
                instagram: "Instagram",
                audience_network: "Audience Network",
                messenger: "Messenger",
                threads: "Threads",
            };
        },
    },
    methods: {
        onDateRangeChange() {
            this.loadAll();
        },
        onMetricChange() {},
        loadAll() {
            this.loadPlatform();
            this.loadPosition();
            this.loadDevice();
        },
        async fetchBreakdown(breakdowns) {
            const res = await INSIGHTS_API.getInsights(
                this.channel._id,
                Object.assign({}, this.dateRange, {
                    level: "account",
                    breakdowns: breakdowns,
                    fields: ["impressions", "reach", "clicks", "spend"],
                    limit: 500,
                })
            );
            return _.get(res, "data.data") || [];
        },
        async loadPlatform() {
            if (!this.channel) return;
            this.loadingPlatform = true;
            try {
                this.platformRows = await this.fetchBreakdown(["publisher_platform"]);
            } catch (err) {
                console.error(err);
                this.platformRows = [];
            } finally {
                this.loadingPlatform = false;
            }
        },
        async loadPosition() {
            if (!this.channel) return;
            this.loadingPosition = true;
            try {
                this.positionRows = await this.fetchBreakdown(["platform_position"]);
            } catch (err) {
                console.error(err);
                this.positionRows = [];
            } finally {
                this.loadingPosition = false;
            }
        },
        async loadDevice() {
            if (!this.channel) return;
            this.loadingDevice = true;
            try {
                this.deviceRows = await this.fetchBreakdown(["impression_device"]);
            } catch (err) {
                console.error(err);
                this.deviceRows = [];
            } finally {
                this.loadingDevice = false;
            }
        },
    },
    mounted() {
        this.loadAll();
    },
};
</script>

<style lang="scss" scoped>
.meta-insights-performance {
    padding: 20px;
    display: flex;
    flex-direction: column;
    gap: 20px;

    .header {
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
        gap: 12px;
        padding-bottom: 14px;
        border-bottom: 1px solid var(--color-border-default);

        .title-block {
            display: flex;
            flex-direction: column;
            gap: 2px;

            .title {
                font-size: 18px;
                font-weight: 700;
                color: var(--color-text-strong-default);
            }

            .subtitle {
                font-size: 13px;
                color: var(--color-text-muted-default);
            }
        }

        .toolbar {
            display: flex;
            align-items: center;
            gap: 8px;
        }
    }

    .grid {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(360px, 1fr));
        gap: 16px;
    }

    @keyframes spin {
        from { transform: rotate(0deg); }
        to { transform: rotate(360deg); }
    }

    .spinning {
        animation: spin 1s linear infinite;
    }
}
</style>
